// Resizing a module from any of its 8 handles (05f) — main.js's resize (Ben's #173), ported
// as a hook on PlacedModule's handles. The module dims and an outline follows the pointer,
// red when the new box would overlap something (or leave the canvas). Released valid, the
// outline snaps to the grid and becomes the module's box; released red, nothing changes.
// ONE undo step, taken at the release and only if the box really changed.
//
// Unlike a move, a resize never opens the canvas, so the release writes the new box
// straight to the store and React draws it (05f's fork 2A) — no page → store copy.

import { useCallback, type PointerEvent as ReactPointerEvent, type RefObject } from "react";
import { flushSync } from "react-dom";
import { useProtoStore } from "../store/store.js";
import { pixelSize } from "./module-shell.js";
import { ensureCanvasPadding, getSnapCoords, isAreaClear } from "./canvas-space.js";
import { moduleIdOf } from "./module-registry.js";
import { refreshResizeDominance } from "./resize-dominance.js";
import { RESIZE_CURSOR_CLASS, blocksFor, resizeOutline, type PxBox, type ResizeDir } from "./resize-rules.js";
import { undoApi } from "./undo.js";
import { getScale as scale } from "./zoom.js";

interface Resize {
  mod: HTMLElement;
  dir: ResizeDir;
  /** The press point in page coordinates. */
  startX: number;
  startY: number;
  start: PxBox;
}

let resize: Resize | null = null;

function snapIndicator(): HTMLElement {
  const el = document.getElementById("snap-indicator");
  if (!el) throw new Error("#snap-indicator missing from legacy-scaffold.html");
  return el;
}

const px = (v: string) => Number.parseInt(v, 10);

function onPointerMove(e: PointerEvent): void {
  const r = resize;
  if (!r) return;
  const s = scale();
  const out = resizeOutline(r.dir, r.start, (e.pageX - r.startX) / s, (e.pageY - r.startY) / s);
  const ind = snapIndicator();
  ind.style.left = `${out.left}px`;
  ind.style.top = `${out.top}px`;
  ind.style.width = `${out.width}px`;
  ind.style.height = `${out.height}px`;
  // Valid or not is judged on the snapped box (and, for 'n' / 'w', its snapped place).
  const snap = getSnapCoords(out.left, out.top);
  if (isAreaClear(snap.col, snap.row, blocksFor(out.width), blocksFor(out.height), r.mod)) {
    ind.classList.remove("invalid", "merge");
  } else {
    ind.classList.add("invalid");
  }
}

function onPointerUp(): void {
  const r = resize;
  if (!r) return;
  resize = null;
  document.removeEventListener("pointermove", onPointerMove);
  document.removeEventListener("pointerup", onPointerUp);
  document.body.classList.remove("is-resizing", "is-resizing-nwse", "is-resizing-nesw", "is-resizing-ns", "is-resizing-ew");

  const { mod } = r;
  const ind = snapIndicator();
  if (!ind.classList.contains("invalid")) {
    // Read back off the outline's style, as main.js did (parseInt drops the fraction).
    const cols = blocksFor(px(ind.style.width));
    const rows = blocksFor(px(ind.style.height));
    const snapped = getSnapCoords(px(ind.style.left), px(ind.style.top));
    const changed =
      mod.style.width !== `${pixelSize(cols)}px` ||
      mod.style.height !== `${pixelSize(rows)}px` ||
      mod.style.left !== `${snapped.x}px` ||
      mod.style.top !== `${snapped.y}px`;
    // Checkpoint BEFORE the change; a release on the same box isn't a step.
    if (changed) undoApi().saveState();
    const id = moduleIdOf(mod);
    if (id === undefined) throw new Error("resize: the module isn't a live one");
    // Synchronous: the padding below measures the new box.
    flushSync(() => useProtoStore.getState().updateModule(id, { x: snapped.x, y: snapped.y, cols, rows }));
    ensureCanvasPadding();
    mod.classList.add("snap-judder");
    mod.addEventListener("animationend", () => mod.classList.remove("snap-judder"), { once: true });
  }

  mod.style.opacity = "";
  ind.style.display = "none";
  ind.classList.remove("invalid", "merge");
  refreshResizeDominance();
}

/** A module is being resized (the handle halo pauses meanwhile). */
export function isResizingModule(): boolean {
  return resize !== null;
}

function startResize(e: PointerEvent, mod: HTMLElement, handle: HTMLElement, dir: ResizeDir): void {
  handle.setPointerCapture(e.pointerId);
  document.body.classList.add("is-resizing", `is-resizing-${RESIZE_CURSOR_CLASS[dir]}`);
  // Nothing reads these any more; main.js wrote them, kept so the element matches.
  mod.dataset.startCols = String(blocksFor(px(mod.style.width)));
  mod.dataset.startRows = String(blocksFor(px(mod.style.height)));

  const start = {
    left: px(mod.style.left || "0"),
    top: px(mod.style.top || "0"),
    width: px(mod.style.width),
    height: px(mod.style.height),
  };
  resize = { mod, dir, startX: e.pageX, startY: e.pageY, start };

  const ind = snapIndicator();
  ind.style.display = "block";
  ind.style.left = `${start.left}px`;
  ind.style.top = `${start.top}px`;
  ind.style.width = mod.style.width;
  ind.style.height = mod.style.height;
  ind.classList.remove("invalid", "merge");
  mod.style.opacity = "0.25";

  document.addEventListener("pointermove", onPointerMove);
  document.addEventListener("pointerup", onPointerUp);
}

/** The handles' onPointerDown for one module. */
export function useModuleResize(
  moduleRef: RefObject<HTMLElement | null>,
): (e: ReactPointerEvent<HTMLElement>, dir: ResizeDir) => void {
  return useCallback(
    (e: ReactPointerEvent<HTMLElement>, dir: ResizeDir) => {
      // As main.js: the press is the handle's alone (on the synthetic event, so React's
      // own bubbling stops too), and no scroll-into-view at a zoom other than 100%.
      e.stopPropagation();
      e.preventDefault();
      const mod = moduleRef.current;
      if (!mod) return;
      startResize(e.nativeEvent, mod, e.currentTarget, dir);
    },
    [moduleRef],
  );
}
